import { useForm } from 'react-hook-form';
import { generatePath, useHistory } from 'react-router-dom';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building, Clock, Image, MapPin } from 'lucide-react';

import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  type SiteCreate,
  SiteCreateSchema,
} from '@/api/fe-api/domain/entities/site';
import { SiteOperationalPhase } from '@/api/fe-api/domain/entities/site-operational-phase';
import { CreateSite } from '@/api/fe-api/view/mutations/create-site';
import { DatePicker } from '@/components/form/inputs/date-picker';
import { NumberInput } from '@/components/form/inputs/number-input';
import { FullPageContent } from '@/components/layout/full-page-content';
import { EnergySuffix } from '@/components/pages/assets/overview/form-edit-asset/energy-suffix';
import { PowerSuffix } from '@/components/pages/assets/overview/form-edit-asset/power-suffix';
import { Card } from '@/components/ui/card';
import { useBlockNavigation } from '@/hooks/use-block-navigation';
import { useMe } from '@/hooks/use-me';
import { formStyle } from '@/styles/form-style';
import { formatMB } from '@/utils/format/bytes';
import { toastResult } from '@/utils/toast';

import { FileUploadBox } from '../../components/pages/site-documents/file-upload-box';

export function CreateSitePage() {
  const history = useHistory();
  const me = useMe();
  const createSite = CreateSite.useMutation();
  const form = useForm<SiteCreate>({
    resolver: zodResolver(SiteCreateSchema),
    defaultValues: {
      operationalPhase: SiteOperationalPhase.COMMISSIONING,
    },
  });

  const { isDirty, isValid } = form.formState;

  useBlockNavigation({
    isDirty: isDirty,
  });

  const style = formStyle();

  return (
    <FullPageContent
      data-testid="create-site-page"
      scrollable={true}
      className="bg-zinc-50"
    >
      <div className="mx-auto my-4 max-w-5xl">
        <Button
          variant="ghost"
          className="-ml-4"
          onClick={() => (window.location.href = '/')}
        >
          <ChevronLeftIcon />
          Back to Home Page
        </Button>
        <div className="py-8">
          <h1 className="mb-2 font-condensed text-2xl font-bold">
            Add a New Asset
          </h1>
          <p className="max-w-prose text-lg text-zinc-500">
            Submit a request to add a new asset.
            <br />
            Our Customer Success Team will get in touch with you. Please have
            the specifications and data sheets ready.
          </p>
        </div>

        <Card>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(async function (data) {
                const result = await toastResult(createSite.mutateAsync(data), {
                  success(_) {
                    return 'Site created successfully';
                  },
                  loading: 'Creating site ...',
                  error(err) {
                    switch (err.type) {
                      case 'EXTERNAL_ERROR': {
                        return (
                          err.description ??
                          'Something went wrong. Please try again later.'
                        );
                      }
                    }
                  },
                  exception() {
                    return 'Something went wrong. Please try again later.';
                  },
                });

                if (result.isOk()) {
                  form.reset();
                  history.push(
                    generatePath('/asset/:siteId', { siteId: result.value.id }),
                  );
                }
              })}
            >
              <div className="space-y-10">
                <div className={style.inputSection()}>
                  <h3 className={style.inputSectionTitle()}>
                    <Building className="text-neutral-400" />
                    Basic Information
                  </h3>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Site name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="operationalPhase"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Operational Phase</FormLabel>

                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="In Operation" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectGroup>
                              {Object.values(SiteOperationalPhase).map(
                                (type) => (
                                  <SelectItem
                                    key={type}
                                    value={type.toString()}
                                  >
                                    {type.toString()}
                                  </SelectItem>
                                ),
                              )}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className={style.inputSection()}>
                    <div className={style.inputSectionRow()}>
                      <FormField
                        control={form.control}
                        name="nominalEnergyWh"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Energy</FormLabel>
                            <FormControl>
                              <NumberInput
                                {...field}
                                value={
                                  typeof field.value === 'number'
                                    ? field.value / 10 ** me.config.powerUnit
                                    : undefined
                                }
                                onChange={function (e) {
                                  const value =
                                    typeof e === 'number'
                                      ? e
                                      : e.currentTarget.value;

                                  if (!value && value !== 0) {
                                    field.onChange(undefined);
                                    return;
                                  }

                                  const nb = Number(value);

                                  field.onChange(
                                    nb * 10 ** me.config.powerUnit,
                                  );
                                }}
                                placeholder="100"
                                rightIcon={<EnergySuffix />}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="nominalPowerW"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Power</FormLabel>
                            <FormControl>
                              <NumberInput
                                {...field}
                                value={
                                  typeof field.value === 'number'
                                    ? field.value / 10 ** me.config.powerUnit
                                    : undefined
                                }
                                onChange={function (e) {
                                  const value =
                                    typeof e === 'number'
                                      ? e
                                      : e.currentTarget.value;

                                  if (!value && value !== 0) {
                                    field.onChange(undefined);
                                    return;
                                  }

                                  const nb = Number(value);

                                  field.onChange(
                                    nb * 10 ** me.config.powerUnit,
                                  );
                                }}
                                placeholder="100"
                                rightIcon={<PowerSuffix />}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className={style.inputSection()}>
                  <h3 className={style.inputSectionTitle()}>
                    <MapPin className="text-neutral-400" />
                    Location Details
                  </h3>

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Av. Gustave Eiffel, 75007 Paris, France"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className={style.inputSectionRow()}>
                    <FormField
                      control={form.control}
                      name="lat"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Latitude</FormLabel>
                          <FormControl>
                            <NumberInput
                              {...field}
                              placeholder="50.7727581"
                              step="any"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lng"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Longitude</FormLabel>
                          <FormControl>
                            <NumberInput
                              {...field}
                              placeholder="6.1102437"
                              step="any"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className={style.inputSection()}>
                  <h3 className={style.inputSectionTitle()}>
                    <Clock className="text-neutral-400" />
                    Project Timeline
                  </h3>
                  <div className={style.inputSectionRow()}>
                    <FormField
                      control={form.control}
                      name="installationDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Installation Date</FormLabel>
                          <FormControl>
                            <DatePicker {...field} onSelect={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="commissioningDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Commissioning Date</FormLabel>
                          <FormControl>
                            <DatePicker {...field} onSelect={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className={style.inputSection()}>
                  <h3 className={style.inputSectionTitle()}>
                    <Image className="text-neutral-400" />
                    Extras
                  </h3>

                  <FormField
                    control={form.control}
                    name="picture"
                    render={({ field: { value, ...field }, fieldState }) => (
                      <FormItem>
                        <FormLabel>
                          Picture{' '}
                          <small className="text-gray-400">Optional</small>
                        </FormLabel>
                        <FormControl>
                          <FileUploadBox
                            {...field}
                            fileName={value?.name}
                            onChange={function (file) {
                              field.onChange(file ?? undefined);
                            }}
                            accept={ALLOWED_FILE_TYPES.join()}
                            hint={
                              fieldState.error?.message
                                ? undefined
                                : `Maximum file size: ${formatMB(MAX_FILE_SIZE)}`
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    className={style.button()}
                    onClick={history.goBack}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="submit"
                    className={style.button()}
                    disabled={!isValid}
                  >
                    Save asset
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </Card>
      </div>
    </FullPageContent>
  );
}
